import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useMsal } from "@azure/msal-react";
import { getB2CRedirectLogoutUrl } from "./../utils/url-constant";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import { useReportInventoryService } from '../services/ReportInventoryServices';
import { setDateInCST } from '../utils/dateUtils';

export const userActivityLogout = (Component: React.ComponentType<any>) => {
    return (props: any) => {
        const timerRef = useRef<number | undefined>(undefined);
        const { instance, accounts } = useMsal();
        const [account] = accounts;
        const alertRef = useRef(false);
        const history = useHistory();
        const [userType, setUserType] = useState<string | null>(null);
        const toastIdRef = useRef<any>(null);
        const [showOverlay, setShowOverlay] = useState(false);
        const isLoggingOutRef = useRef(false);
        const ReportInventoryService = useReportInventoryService();
        const isB2CUser = userType === 'HealthPlanUser';
        const location = useLocation();

        const getPageNameFromUrl = () => {
            const pathname = window.location.pathname;

            const pathMap: { [key: string]: string } = {
                '/action-details': 'action_details',
                '/adhoc-or-new-report': 'add_inventory_report',
                '/': 'admin_dashboard',
                '/admin-dashboard': 'admin_dashboard',
                '/mco-actions': 'mco_actions_page',
                '/reminders-notifications': 'reminders_alerts',
                '/contract-report-details': 'report_detail_page',
                '/contact-report-inventory': 'report_inventory_listpage',
                '/reference-data': 'report_master_catalog_page',
                '/report-catalog-details': 'report_master_detail_page',
                '/mco-review': 'review_page',
                '/upload-reports': 'upload_submit_reports',
                '/user-management-details': 'user_detail_page',
                '/user-management': 'user_inventory_page',
            };

            // Check exact match
            if (pathMap[pathname]) {
                return pathMap[pathname];
            }

            return 'admin_dashboard';
        };

        useEffect(() => {
            const checkUserType = () => {
                const storedUserType = sessionStorage.getItem('signedInUserType');

                if (storedUserType !== userType) {
                    setUserType(storedUserType);
                }
            };


            checkUserType();

            // Check every 30 seconds 
            const interval = setInterval(checkUserType, 30000);

            window.addEventListener('storage', checkUserType);


            const handleCustomStorageEvent = (e: CustomEvent) => {
                if (e.detail.key === 'signedInUserType') {
                    checkUserType();
                }
            };

            window.addEventListener('sessionStorageUpdate', handleCustomStorageEvent as EventListener);

            return () => {
                clearInterval(interval);
                window.removeEventListener('storage', checkUserType);
                window.removeEventListener('sessionStorageUpdate', handleCustomStorageEvent as EventListener);
            };
        }, [userType]);

        const handleLogout = useCallback(async () => {
            const loggedInUserDetails = JSON.parse(sessionStorage.getItem('loginUserDetails') || '{}');
            const loggedInUserIPDetails = JSON.parse(sessionStorage.getItem('loginUserIPDetails') || '{}');

            const createAuditLogData = () => ({
                userId: loggedInUserDetails?.userEmail || '',
                userSid: loggedInUserDetails?.userId || '',
                accountTypeLkpcd: loggedInUserDetails?.accountTypeLkpcd || '',
                userIpv4Address: loggedInUserIPDetails?.ipAddress || '',
                userIpv6Address: loggedInUserIPDetails?.ipAddress || '',
                userMacAddress: loggedInUserIPDetails?.userMacAddress || null,
                userHostName: window?.location?.host || window?.location?.hostname || '',
                userRequestHeaderJson: null,
                eventLkpcd: 'TO',
                eventStatusLkpcd: 'S',
                pageName: getPageNameFromUrl(),
                componentApiName: null,
                componentApiAttributesJson: null,
                errorJson: null,
                mctReportInstanceSid: null,
                mctReportActivitySid: null,
                mctReportSubactivitySid: null,
                capturedScreenFileName: null,
                capturedScreenFileLocation: null,
                createdBy: loggedInUserDetails?.userEmail || '',
                createdDate: setDateInCST(new Date(), 'YYYY-MM-DDTHH:mm:ss'),
            });

            isLoggingOutRef.current = true;
            setShowOverlay(false);

            if (account && !isB2CUser) {
                await ReportInventoryService.createAuditLog(createAuditLogData());

                sessionStorage.clear()
                localStorage.clear()

                // Remove cookies
                const removeCookie = (name: string) => {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                };

                const cookiesToRemove = ['token'];

                cookiesToRemove.forEach(removeCookie);

                instance.logoutRedirect({
                    account,
                    postLogoutRedirectUri: "/sign-in",
                });
                alertRef.current = false;
            } else if (isB2CUser && !account) {

                await ReportInventoryService.createAuditLog(createAuditLogData());

                sessionStorage.clear();
                localStorage.clear();


                const removeCookie = (name: string) => {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                };

                const cookiesToRemove = ['token'];
                cookiesToRemove.forEach(removeCookie);

                const signOutUrl = getB2CRedirectLogoutUrl();
                if (signOutUrl) {
                    setTimeout(() => {
                        window.location.href = signOutUrl;
                    }, 100);
                } else {
                    console.error("Sign-in URL is undefined");
                    history.push("/sign-in");
                }
                alertRef.current = false;
            }
        }, [instance, account, isB2CUser, history]);


        const handleAlert = useCallback(async () => {
            if ((account || isB2CUser) && !alertRef.current && !toastIdRef.current) {
                alertRef.current = true;
                setShowOverlay(true);

                // Mark session as expired in sessionStorage
                sessionStorage.setItem('sessionExpired', new Date().toISOString());

                const toastId = toast.warn(
                    <div style={{ position: 'relative', width: '300px' }}>
                        <div style={{
                            marginBottom: '15px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#822405ff',
                            // textAlign: 'center',
                            borderBottom: '1px solid #ffeaa7',
                            paddingBottom: '10px'
                        }}>
                            Session Expired
                        </div>
                        <div style={{ marginBottom: '15px', fontSize: '14px', lineHeight: '1.5', color: '#822405ff' }}>
                            <div style={{ marginBottom: '5px' }}>Your session has expired due to inactivity.</div>
                            <div>You need to login again to start over.</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <button
                                onClick={() => {
                                    toast.dismiss(toastId);
                                    toastIdRef.current = null;
                                    setShowOverlay(false);
                                    handleLogout();
                                }}
                                style={{
                                    padding: '8px 20px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    position: 'relative', // Ensure button can be clicked
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>,
                    {
                        autoClose: false,
                        closeOnClick: false,
                        closeButton: false,
                        position: "top-center",
                        style: {
                            pointerEvents: 'auto'
                        },
                        onClose: () => {

                            toastIdRef.current = null;
                            alertRef.current = false;
                            setShowOverlay(false);
                        }
                    }
                );

                toastIdRef.current = toastId;
            }
        }, [handleLogout, account, isB2CUser]);


        useEffect(() => {
            if (showOverlay) {

                const preventKeyboard = (e: KeyboardEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                };


                const preventContextMenu = (e: MouseEvent) => {
                    e.preventDefault();
                    return false;
                };


                const preventUnload = (e: BeforeUnloadEvent) => {

                    if (isLoggingOutRef.current) {
                        return;
                    }
                    e.preventDefault();
                    e.returnValue = '';
                    return '';
                };

                // Add event listeners
                document.addEventListener('keydown', preventKeyboard, true);
                document.addEventListener('contextmenu', preventContextMenu, true);
                window.addEventListener('beforeunload', preventUnload, true);

                return () => {
                    document.removeEventListener('keydown', preventKeyboard, true);
                    document.removeEventListener('contextmenu', preventContextMenu, true);
                    window.removeEventListener('beforeunload', preventUnload, true);
                };
            }
        }, [showOverlay]);

        // Check if session was already expired (e.g., after page refresh)
        useEffect(() => {
            const sessionExpired = sessionStorage.getItem('sessionExpired');
            if (sessionExpired && (account || isB2CUser)) {
                // Session has expired, force logout immediately
                console.log("Session already expired, forcing logout");
                handleLogout();
            }
        }, [account, isB2CUser, handleLogout]);

        useEffect(() => {

            alertRef.current = false;
            toastIdRef.current = null;
            setShowOverlay(false);

            const resetTimer = () => {

                if (alertRef.current || toastIdRef.current) {
                    console.log("Alert/Toast already active, not resetting timer");
                    return;
                }

                if (timerRef.current !== undefined) {
                    clearTimeout(timerRef.current);
                }

                timerRef.current = window.setTimeout(handleAlert, 1800000); // 30 minutes
            };

            if (account || isB2CUser) {
                const events = ['mousemove', 'keydown', 'scroll', 'click'];

                events.forEach(event => {
                    window.addEventListener(event, resetTimer);
                });

                resetTimer();

                // Cleanup function
                return () => {
                    if (timerRef.current !== undefined) {
                        clearTimeout(timerRef.current);
                    }
                    events.forEach(event => {
                        window.removeEventListener(event, resetTimer);
                    });

                    if (toastIdRef.current) {
                        toast.dismiss(toastIdRef.current);
                        toastIdRef.current = null;
                    }
                    setShowOverlay(false);
                };
            } else {
                console.log("No active user session, skipping activity monitoring");
            }
        }, [handleAlert, account, isB2CUser]);

        return (
            <>
                <Component {...props} />
                {showOverlay && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            // zIndex: 99998,
                            backdropFilter: 'blur(5px)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    />
                )}
            </>
        );
    };
};